import { Box } from '@mui/material';
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CourseSkeleton } from './CourseSkeleton';
import { DocIcon } from './DocIcon';
import { formatEntryMeta } from './entryMeta';
import { NavToggleButton } from './NavToggleButton';
import { useCourseProgress } from './useCourseProgress';

const lessonMinutes = (entry) =>
  entry.contentType === 'video' && entry.videoDurationSeconds != null
    ? Math.ceil(entry.videoDurationSeconds / 60)
    : entry.readTimeMinutes || 0;

export const CourseView = () => {
  const { courseSlug } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setNavOpen, courses } = useOutletContext();

  const { isDone } = useCourseProgress(courseSlug);
  const course = courses?.find((item) => item.slug === courseSlug);

  if (!courses) return <CourseSkeleton />;

  if (!course)
    return (
      <Box component="main" className="flex-1 min-w-0 h-full overflow-y-auto">
        <Box className="mx-auto text-center" sx={{ maxWidth: '780px', padding: '80px 40px' }}>
          <Box sx={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
            {t('This documentation entry could not be found.')}
          </Box>
          <Link to="/documentation">{t('Help Center')}</Link>
        </Box>
      </Box>
    );

  // Lesson numbering is sequential across sections, in order
  const lessons = course.sections.flatMap((section) => section.lessons);
  const doneCount = lessons.filter((lesson) => isDone(lesson.entry.slug)).length;
  const percent = lessons.length
    ? Math.round((doneCount / lessons.length) * 100)
    : 0;
  const nextLesson = lessons.find((lesson) => !isDone(lesson.entry.slug));
  const nextLessonNumber = nextLesson ? lessons.indexOf(nextLesson) + 1 : null;
  const remainingMinutes = lessons
    .filter((lesson) => !isDone(lesson.entry.slug))
    .reduce((sum, lesson) => sum + lessonMinutes(lesson.entry), 0);

  const openLesson = (entrySlug) =>
    navigate(`/documentation/articles/${entrySlug}?course=${courseSlug}`);

  let lessonNumber = 0;

  return (
    <Box component="main" className="flex-1 min-w-0 h-full overflow-y-auto">
      <Box className="mx-auto" sx={{ maxWidth: '780px', padding: '28px 40px 80px' }}>
        <Box
          className="flex items-center flex-wrap"
          sx={{ gap: '8px', fontSize: '12px', color: 'var(--text2)' }}
        >
          <NavToggleButton onClick={() => setNavOpen(true)} />
          <Link to="/documentation">{t('Help Center')}</Link>
          <DocIcon name="chevron_right" size={14} color="var(--text2)" />
          <span>{t('Courses')}</span>
          <DocIcon name="chevron_right" size={14} color="var(--text2)" />
          <Box component="span" sx={{ color: 'var(--text)', fontWeight: 500 }}>
            {course.title}
          </Box>
        </Box>

        <Box
          component="h1"
          sx={{ fontSize: '28px', fontWeight: 700, lineHeight: 1.25, margin: '18px 0 6px' }}
        >
          {course.title}
        </Box>
        {course.description && (
          <Box
            component="p"
            sx={{
              fontSize: '13.5px',
              color: 'var(--text2)',
              margin: '0 0 22px',
              maxWidth: '560px',
              textWrap: 'pretty',
            }}
          >
            {course.description}
          </Box>
        )}

        <Box
          className="flex items-center flex-wrap"
          sx={{
            border: '1px solid var(--border)',
            borderRadius: '12px',
            background: 'var(--panel)',
            boxShadow: 'var(--shadow)',
            padding: '18px 20px',
            marginBottom: '28px',
            gap: '20px',
          }}
        >
          <Box className="flex-1" sx={{ minWidth: '220px' }}>
            <Box
              className="flex justify-between"
              sx={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}
            >
              <span>
                {t('{{done}} of {{total}} completed', {
                  done: doneCount,
                  total: lessons.length,
                })}
              </span>
              <Box component="span" sx={{ color: 'var(--text2)' }}>
                {percent} %
              </Box>
            </Box>
            <Box
              className="overflow-hidden"
              sx={{
                height: '7px',
                borderRadius: '99px',
                background: 'var(--panel2)',
                border: '1px solid var(--border)',
              }}
            >
              <Box
                sx={{
                  width: `${percent}%`,
                  height: '100%',
                  background: 'var(--accent)',
                  borderRadius: '99px',
                }}
              />
            </Box>
            {remainingMinutes > 0 && (
              <Box sx={{ fontSize: '11px', color: 'var(--text2)', marginTop: '8px' }}>
                {t('About {{n}} min. remaining', { n: remainingMinutes })}
              </Box>
            )}
          </Box>
          {nextLesson && (
            <Box
              component="button"
              onClick={() => openLesson(nextLesson.entry.slug)}
              className="inline-flex items-center cursor-pointer"
              sx={{
                gap: '8px',
                border: 'none',
                background: 'var(--accent)',
                color: '#ffffff',
                fontFamily: 'inherit',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '10px',
                padding: '11px 20px',
                ':hover': { filter: 'brightness(1.06)' },
              }}
            >
              <DocIcon name="play_arrow" size={19} />
              {t('Resume · Lesson {{n}}', { n: nextLessonNumber })}
            </Box>
          )}
        </Box>

        {course.sections.map((section) => (
          <Box key={section.id} sx={{ marginBottom: '26px' }}>
            <Box
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: 'var(--text2)',
                marginBottom: '10px',
              }}
            >
              {section.name}
            </Box>
            <Box className="flex flex-col" sx={{ gap: '8px' }}>
              {section.lessons.map((lesson) => {
                lessonNumber += 1;
                const currentNumber = lessonNumber;
                const entry = lesson.entry;
                const done = isDone(entry.slug);
                const current = nextLesson && lesson === nextLesson;
                return (
                  <Box
                    key={entry.slug}
                    className="flex items-center cursor-pointer"
                    onClick={() => openLesson(entry.slug)}
                    sx={{
                      gap: '14px',
                      border: `1px solid ${current ? 'var(--accent-border)' : 'var(--border)'}`,
                      background: current ? 'var(--accent-soft)' : 'var(--panel)',
                      borderRadius: '11px',
                      padding: '13px 16px',
                    }}
                  >
                    <Box
                      component="span"
                      className="inline-flex flex-none items-center justify-center"
                      sx={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        background: done
                          ? 'rgba(67,160,71,.12)'
                          : current
                            ? 'var(--accent)'
                            : 'var(--panel2)',
                        color: done
                          ? 'var(--green)'
                          : current
                            ? '#ffffff'
                            : 'var(--text2)',
                        border: `1px solid ${
                          done
                            ? 'rgba(67,160,71,.4)'
                            : current
                              ? 'var(--accent)'
                              : 'var(--border)'
                        }`,
                        fontSize: '12.5px',
                        fontWeight: 700,
                      }}
                    >
                      {done ? <DocIcon name="check" size={17} /> : currentNumber}
                    </Box>
                    <Box className="flex-1 min-w-0">
                      <Box
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          textDecoration: done ? 'line-through' : 'none',
                          color: done ? 'var(--text2)' : 'var(--text)',
                        }}
                      >
                        {entry.title}
                      </Box>
                      <Box
                        className="flex items-center"
                        sx={{ gap: '10px', fontSize: '11px', color: 'var(--text2)', marginTop: '3px' }}
                      >
                        <Box component="span" className="inline-flex items-center" sx={{ gap: '4px' }}>
                          <DocIcon
                            name={
                              entry.contentType === 'video'
                                ? 'play_circle'
                                : 'article'
                            }
                            size={14}
                          />
                          {formatEntryMeta(entry, t)}
                        </Box>
                      </Box>
                    </Box>
                    {current ? (
                      <Box
                        component="span"
                        className="inline-flex items-center"
                        sx={{
                          gap: '6px',
                          border: '1px solid var(--accent-border)',
                          background: 'var(--accent-soft)',
                          color: 'var(--accent-strong)',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          borderRadius: '8px',
                          padding: '7px 13px',
                        }}
                      >
                        {t('Resume')}
                      </Box>
                    ) : (
                      !done && (
                        <DocIcon name="chevron_right" size={18} color="var(--text2)" />
                      )
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
